/** @file useLastRoute: remembers the last puppet screen; reopens it on a bare start if Settings says so. */
import { useEffect } from 'react';
import type { EditorStore, Route } from '../state/editorTypes';
import { sameTile } from '../state/sameTile';
import { EDITOR_SETTINGS } from './editorSettings';
import { parseHash } from './router/parseHash';
import { routeHash } from './router/routeHash';

/** A saved Tile route reopens only while its tab is still in that puppet's stored tabs; otherwise its Pose screen. */
function reopenable(route: Route): Route | null {
  if (route.screen === 'pose' || route.screen === 'art' || route.screen === 'agent') return route;
  if (route.screen !== 'tile') return null;
  const tab = route.tile;
  return EDITOR_SETTINGS.tiles(route.puppetId).load().some((t) => sameTile(t, tab)) ? route : { screen: 'pose', puppetId: route.puppetId };
}

/**
 * Every puppet-screen route (Tile tabs too) is saved as `pose3d.lastRoute`. At mount, when `pose3d.reopenLast` is on, the page has no
 * link of its own (empty hash or `#/projects`) and a route was saved, the store navigates there. Run it after
 * `useHashRoute`, which has already taken the hash.
 */
export function useLastRoute(store: EditorStore, hash: () => string = () => window.location.hash): void {
  useEffect(() => {
    const bare = ['', '#', '#/', '#/projects'].includes(hash());
    const saved = EDITOR_SETTINGS.lastRoute.load();
    if (bare && saved !== null && EDITOR_SETTINGS.reopenLast.load()) {
      const route = reopenable(parseHash(saved));
      if (route !== null) store.getState().navigate(route);
    }
    return store.subscribe((s, prev) => {
      if (s.route === prev.route) return;
      const r = s.route.screen;
      if (r === 'pose' || r === 'art' || r === 'agent' || r === 'tile') EDITOR_SETTINGS.lastRoute.save(routeHash(s.route));
    });
  }, [store, hash]);
}
